import React from 'react';
import GenericStatusPage from './GenericStatusPage';
import {
  ErrorType,
  ErrorConfigMap,
  GenericStatusPageAction,
  GenericStatusPageProps
} from './types';

// Configuration par défaut pour chaque type d'erreur
const defaultErrorConfigs: ErrorConfigMap = {
  [ErrorType.NOT_FOUND]: {
    statusCode: '404',
    title: 'Page non trouvée',
    message: 'La page que vous recherchez n\'existe pas',
    description: 'Il se peut que l\'URL soit incorrecte ou que la page ait été supprimée.',
    icon: '🔍',
    iconColor: 'text-primary',
  },
  [ErrorType.UNAUTHORIZED]: {
    statusCode: '401',
    title: 'Non authentifié',
    message: 'Vous devez vous connecter pour accéder à cette page',
    description: 'Votre session a peut-être expiré.',
    icon: '🔐',
    iconColor: 'text-yellow-500',
    defaultActions: [
      {
        label: 'Se connecter',
        onClick: () => window.location.href = '/login',
        variant: 'primary'
      }
    ]
  },
  [ErrorType.FORBIDDEN]: {
    statusCode: '403',
    title: 'Accès refusé',
    message: 'Vous n\'avez pas les permissions nécessaires',
    description: 'Contactez votre administrateur si vous pensez que c\'est une erreur.',
    icon: '🔒',
    iconColor: 'text-red-500',
    defaultActions: [
      {
        label: 'Contacter le support',
        onClick: () => window.location.href = 'mailto:support@example.com',
        variant: 'danger'
      }
    ]
  },
  [ErrorType.SERVER_ERROR]: {
    statusCode: '500',
    title: 'Erreur interne',
    message: 'Une erreur inattendue s\'est produite',
    description: 'Nous travaillons pour résoudre ce problème. Veuillez réessayer dans quelques instants.',
    icon: '⚠️',
    iconColor: 'text-yellow-500',
    defaultActions: [
      {
        label: 'Réessayer',
        onClick: () => window.location.reload(),
        variant: 'warning',
        icon: '🔄'
      }
    ]
  },
  [ErrorType.NETWORK_ERROR]: {
    title: 'Problème de connexion',
    message: 'Impossible de se connecter au serveur',
    description: 'Vérifiez votre connexion internet et réessayez.',
    icon: '📡',
    iconColor: 'text-gray-500',
    defaultActions: [
      {
        label: 'Réessayer',
        onClick: () => window.location.reload(),
        variant: 'primary',
        icon: '🔄'
      }
    ]
  },
  [ErrorType.SESSION_EXPIRED]: {
    title: 'Session expirée',
    message: 'Votre session a expiré',
    description: 'Pour des raisons de sécurité, vous devez vous reconnecter.',
    icon: '⏰',
    iconColor: 'text-indigo-500',
    defaultActions: [
      {
        label: 'Se reconnecter',
        onClick: () => window.location.href = '/login',
        variant: 'primary'
      }
    ]
  },
  [ErrorType.MAINTENANCE]: {
    title: 'Maintenance en cours',
    message: 'Le site est temporairement indisponible',
    description: 'Nous effectuons une maintenance programmée. Le service sera rétabli sous peu.',
    icon: '🔧',
    iconColor: 'text-orange-500',
    defaultActions: [
      {
        label: 'Statut du service',
        onClick: () => window.open('https://status.example.com', '_blank'),
        variant: 'outline'
      }
    ]
  }
};

// Interface pour le GenericStatusPageFactory
interface GenericStatusPageFactoryProps {
  type: ErrorType;
  overrides?: Partial<GenericStatusPageProps>;
  additionalActions?: GenericStatusPageAction[];
  hideDefaultActions?: boolean;
}

// Factory pour créer des pages de statut
export const GenericStatusPageFactory: React.FC<GenericStatusPageFactoryProps> = ({
  type,
  overrides = {},
  additionalActions = [],
  hideDefaultActions = false
}) => {
  const config = defaultErrorConfigs[type];

  if (!config) {
    console.error(`Configuration non trouvée pour le type d'erreur: ${type}`);
    return null;
  }

  // Fusionner les actions par défaut avec les actions supplémentaires
  const actions: GenericStatusPageAction[] = [
    ...(hideDefaultActions ? [] : (config.defaultActions || [])),
    ...additionalActions
  ];

  // Fusionner la configuration par défaut avec les overrides
  const props: GenericStatusPageProps = {
    ...config,
    ...overrides,
    actions: overrides.actions || actions,
    showBackButton: overrides.showBackButton ?? (type !== ErrorType.SESSION_EXPIRED && type !== ErrorType.MAINTENANCE)
  };

  return <GenericStatusPage {...props} />;
};

// Composants pré-configurés utilisant le factory
export const NotFoundPage: React.FC<{ customActions?: GenericStatusPageAction[] }> = ({ customActions }) => (
  <GenericStatusPageFactory
    type={ErrorType.NOT_FOUND}
    additionalActions={customActions || []}
  />
);

export const UnauthorizedPage: React.FC<{ supportEmail?: string }> = ({ supportEmail }) => (
  <GenericStatusPageFactory
    type={ErrorType.FORBIDDEN}
    overrides={supportEmail ? {
      actions: [{
        label: 'Contacter le support',
        onClick: () => window.location.href = `mailto:${supportEmail}`,
        variant: 'danger'
      }]
    } : undefined}
  />
);

export const ServerErrorPage: React.FC<{ errorId?: string; onRetry?: () => void }> = ({
  errorId,
  onRetry
}) => (
  <GenericStatusPageFactory
    type={ErrorType.SERVER_ERROR}
    overrides={{
      description: errorId
        ? `Nous travaillons pour résoudre ce problème. ID d'erreur: ${errorId}`
        : undefined,
      actions: onRetry ? [{
        label: 'Réessayer',
        onClick: onRetry,
        variant: 'warning',
        icon: '🔄'
      }] : undefined
    }}
  />
);

export const NetworkErrorPage: React.FC = () => (
  <GenericStatusPageFactory type={ErrorType.NETWORK_ERROR} />
);

export const SessionExpiredPage: React.FC = () => (
  <GenericStatusPageFactory type={ErrorType.SESSION_EXPIRED} />
);

export const MaintenancePage: React.FC<{
  estimatedDuration?: string;
  statusUrl?: string;
}> = ({ estimatedDuration, statusUrl }) => (
  <GenericStatusPageFactory
    type={ErrorType.MAINTENANCE}
    overrides={{
      description: estimatedDuration
        ? `Maintenance programmée en cours. Durée estimée: ${estimatedDuration}`
        : undefined,
      actions: statusUrl ? [{
        label: 'Statut du service',
        onClick: () => window.open(statusUrl, '_blank'),
        variant: 'outline'
      }] : undefined
    }}
  />
);

// Hook pour utiliser le factory de manière programmatique
export const useGenericStatusPage = () => {
  const createGenericStatusPage = React.useCallback((
    type: ErrorType,
    options?: Omit<GenericStatusPageFactoryProps, 'type'>
  ) => {
    return <GenericStatusPageFactory type={type} {...options} />;
  }, []);

  return { createGenericStatusPage };
};

// Export du type pour utilisation externe
export { ErrorType };
